import { Button, Form, Input } from 'antd'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/components'
import { useUserStore } from '@/stores/user'

interface ExchangeModal {
  onOk: () => void
  onClose: () => void
}

export function ExchangeModal(props: ExchangeModal) {
  const { onOk, onClose } = props
  const [form] = Form.useForm<any>()
  const [availableBits, setAvailableBits] = useState(0)
  const [expectedBits, setExpectedBits] = useState(0)
  const { t } = useTranslation()

  const userStore = useUserStore()

  useEffect(() => {
    setAvailableBits(userStore.invitationInfo.totalInvited)
  }, [userStore.invitationInfo.totalInvited])

  const _onOK = async () => {
    onOk()
  }

  return (
    <Modal
      className="w-[640px]"
      open={true}
      onClose={() => {
        onClose()
      }}
    >
      <Modal.Header>
        <Trans i18nKey="profile.exchangeModal.title">Credits Exchange</Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="max-h-[540px] overflow-y-auto scrollbar-hide">
          <div className="h-[124px] p-6 bg-[url(@/assets/images/bg-recharge-header.png)] rounded-2xl border border-blue-50 flex flex-col items-center justify-center mb-4">
            <p className="t-sm text-secondary mb-4">
              <Trans i18nKey="profile.exchangeModal.availableBits">Available Bits</Trans>
            </p>
            <div className="text-primary flex items-baseline gap-2">
              <p className="text-[28px] leading-10">{availableBits}</p>
              <p className="t-sm">{availableBits > 1 ? 'Bits' : 'Bit'}</p>
            </div>
          </div>
          <Form
            layout="vertical"
            form={form}
            initialValues={{ bits: 0 }}
            validateTrigger="onBlur"
            scrollToFirstError
            onValuesChange={() => {
              form.validateFields(['bits'])
            }}
          >
            <Form.Item
              label={
                <div className="text-with-star">
                  <Trans i18nKey="profile.exchangeModal.form.amount">Bits For Exchange</Trans>
                </div>
              }
              name="bits"
              rules={[
                { required: true, message: t('profile.exchangeModal.form.amountPlaceholder') },
                {
                  validator: (_, value) => {
                    if (Number.isNaN(Number(value))) {
                      return Promise.reject(new Error('Please enter a number'))
                    }
                    if (value > availableBits) {
                      return Promise.reject(new Error('Input value exceeds available bits'))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Input
                size="large"
                suffix={
                  <p
                    className="t-sb text-primary cursor-pointer"
                    onClick={() => {
                      form.setFieldsValue({ bits: availableBits })
                      setExpectedBits(availableBits)
                    }}
                  >
                    <Trans i18nKey="profile.exchangeModal.form.exchangeAllBtn">Exchange All</Trans>
                  </p>
                }
                inputMode="numeric"
                maxLength={9}
                max={1_0000_0000}
                className="col-span-2 items-center t-sb px-4 py-3 h-11 border rounded-lg hover:border-secondary"
                onChange={(e) => {
                  const value = Number(e.target.value)
                  setExpectedBits(value)
                }}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal.Body>
      <div>
        <div className="pt-4 flex items-end justify-end gap-4">
          <div className="flex flex-col flex-grow">
            <div className="text-[24px] leading-10 font-bold text-blue-500 gap-2">
              {Number.isNaN(expectedBits) ? 0 : expectedBits}{' '}
              <span className="t-sm">{expectedBits > 1 ? 'Bits' : 'Bit'}</span>
            </div>
            <div className="t-s-m text-tertiary">
              <Trans i18nKey="profile.exchangeModal.creditsEquivalent">Credits equivalent</Trans>
            </div>
          </div>
          <Button
            size="large"
            className="t-sb"
            onClick={() => {
              onClose()
            }}
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type="primary"
            size="large"
            disabled={form.getFieldValue('bits') < 1 || form.getFieldValue('bits') > availableBits}
            onClick={_onOK}
          >
            <Trans i18nKey="common.confirmModalRecharge">Exchange</Trans>
          </Button>
        </div>
      </div>
    </Modal>
  )
}
